import { fetchCustomerCreditLedger } from '../../services/customerCreditService';
import { invoiceStatusMapFromLedger } from './invoiceStatusMapFromLedger';

/**
 * ONE shared, memory-only store for the tenant's credit ledger (RD-CX-022).
 *
 * Every DISPLAY reader (Credits tab, dashboard tile, customer-profile row,
 * invoice sidebar) reads this store, so one download feeds them all and a
 * live refresh (useCreditsLive) updates every screen at once. MONEY paths
 * (send-time credit, bulk send) deliberately do NOT read it — they keep
 * their own fresh `fetchCustomerCreditLedger`.
 *
 * - Memory only (never localStorage); cleared at sign-out.
 * - A tenantId change resets it; responses for a previous tenant/generation
 *   are ignored.
 * - A failed load AFTER a success keeps the last good data (status stays
 *   'ready', `error` records it). Only a failure before ANY success is
 *   status 'error'.
 * - An optimistic write applied mid-flight also queues a rerun, and the older
 *   in-flight response is dropped (never painted) once a rerun is queued.
 * - A `force` load requested while one is in flight re-runs once after it, so
 *   a write that landed mid-flight is never masked by an older response.
 */
const EMPTY = Object.freeze({
  tenantId: null,
  entries: [],
  invoiceStatusById: {},
  status: 'idle',
  everLoaded: false,
  loadedAt: 0,
  error: null,
});

let state = EMPTY;
let generation = 0;
let inflight = null; // { tenantId, promise, rerun }
const listeners = new Set();

function setState(next) {
  state = next;
  [...listeners].forEach((fn) => { try { fn(); } catch (_) { /* never break a reader */ } });
}

export function getCreditsSnapshot() { return state; }

export function subscribeCredits(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clearCreditsStore() {
  generation += 1;
  inflight = null;
  if (state !== EMPTY) setState(EMPTY);
}

function resetForTenant(tenantId) {
  generation += 1;
  inflight = null;
  setState({ ...EMPTY, tenantId });
}

async function runLoad(tenantId, gen, entry) {
  const loadingNow = state.tenantId === tenantId && !state.everLoaded;
  if (loadingNow && state.status !== 'loading') setState({ ...state, status: 'loading' });
  let res;
  try {
    res = await fetchCustomerCreditLedger(tenantId);
  } catch (err) {
    res = { data: null, error: err };
  }
  if (gen !== generation || state.tenantId !== tenantId) return; // stale
  // A newer read is already queued (a force load or an optimistic write landed
  // mid-flight): this response is OLDER than that write, so painting it would
  // flash the stale figure / wipe an optimistic row. The queued rerun writes.
  // The first-ever load always writes (nothing to protect, and no data yet).
  if (entry?.rerun && state.everLoaded) return;
  if (res?.error) {
    // eslint-disable-next-line no-console
    console.warn('[customerCreditsStore] ledger load failed:', res.error);
    setState(state.everLoaded
      ? { ...state, error: res.error }
      : { ...state, status: 'error', error: res.error });
    return;
  }
  const data = Array.isArray(res?.data) ? res.data : [];
  setState({
    tenantId,
    entries: data,
    invoiceStatusById: invoiceStatusMapFromLedger(data),
    status: 'ready',
    everLoaded: true,
    loadedAt: Date.now(),
    error: null,
  });
}

/**
 * Load (or refresh) the tenant ledger. Non-force is a no-op when already
 * 'ready' for this tenant. Concurrent calls share one request. `coalesce`
 * (pull refresh callers): a force load that finds one in flight just joins it
 * instead of queueing a rerun, so a pull issues ONE ledger request.
 */
export function loadCredits(tenantId, { force = false, coalesce = false } = {}) {
  if (!tenantId) return Promise.resolve();
  if (state.tenantId !== tenantId) resetForTenant(tenantId);
  if (!force && state.status === 'ready') return Promise.resolve();
  if (inflight && inflight.tenantId === tenantId) {
    if (force && !coalesce) inflight.rerun = true;
    return inflight.promise;
  }
  const gen = generation;
  const entry = { tenantId, rerun: false, promise: null };
  entry.promise = (async () => {
    try {
      await runLoad(tenantId, gen, entry);
      while (entry.rerun && gen === generation) {
        entry.rerun = false;
        // eslint-disable-next-line no-await-in-loop
        await runLoad(tenantId, gen, entry);
      }
    } finally {
      if (inflight === entry) inflight = null;
    }
  })();
  inflight = entry;
  return entry.promise;
}

/** Prepend locally-built rows the instant a write succeeds (reconcile follows). */
export function applyOptimisticEntries(rows) {
  // Skip rows the store already holds (a grant carries its SERVER id, and a
  // live reload can land before the optimistic patch — never count it twice).
  const known = new Set(state.entries.map((e) => e?.id).filter(Boolean));
  const list = Array.isArray(rows) ? rows.filter((r) => r && !(r.id && known.has(r.id))) : [];
  if (!list.length || !state.tenantId) return;
  // An older in-flight response must not overwrite these rows: queue a rerun.
  if (inflight && inflight.tenantId === state.tenantId) inflight.rerun = true;
  setState({ ...state, entries: [...list, ...state.entries] });
}
